import { Injectable, inject } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { ComponentType } from '@angular/cdk/portal';
import { ConfirmationModalComponent } from '../components/confirmation-modal/confirmation-modal.component';
import { ErrorModalComponent } from '../components/error-modal/error-modal.component';
import { SuccessModalComponent } from '../components/success-modal/success-modal.component';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);

  /**
   * Muestra un modal
   */
  showModal<T>(component: ComponentType<T>, config?: MatDialogConfig): MatDialogRef<T> {
    const defaultConfig: MatDialogConfig = {
      width: '600px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      disableClose: false,
      autoFocus: true,
      panelClass: 'custom-modal-panel'
    };

    const dialogConfig = { ...defaultConfig, ...config };
    return this.dialog.open(component, dialogConfig);
  }

  /**
   * Muestra una notificación de éxito
   */
  showSuccess(message: string, duration: number = 3000): void {
    const config: MatSnackBarConfig = {
      duration,
      panelClass: ['success-snackbar'],
      horizontalPosition: 'end',
      verticalPosition: 'top'
    };
    this.snackBar.open(message, 'Cerrar', config);
  }

  /**
   * Muestra una notificación de error
   */
  showError(message: string, duration: number = 5000): void {
    const config: MatSnackBarConfig = {
      duration,
      panelClass: ['error-snackbar'],
      horizontalPosition: 'end',
      verticalPosition: 'top'
    };
    this.snackBar.open(message, 'Cerrar', config);
  }

  /**
   * Muestra una notificación de advertencia
   */
  showWarning(message: string, duration: number = 4000): void {
    const config: MatSnackBarConfig = {
      duration,
      panelClass: ['warning-snackbar'],
      horizontalPosition: 'end',
      verticalPosition: 'top'
    };
    this.snackBar.open(message, 'Cerrar', config);
  }

  /**
   * Muestra una notificación de información
   */
  showInfo(message: string, duration: number = 3000): void {
    const config: MatSnackBarConfig = {
      duration,
      panelClass: ['info-snackbar'],
      horizontalPosition: 'end',
      verticalPosition: 'top'
    };
    this.snackBar.open(message, 'Cerrar', config);
  }

  /**
   * Muestra un diálogo de confirmación
   */
  showConfirmation(message: string, title: string = 'Confirmar'): Promise<boolean> {
    return new Promise(resolve => {
      const dialogRef = this.dialog.open(ConfirmationModalComponent, {
        width: '450px',
        data: { message, title },
        panelClass: 'custom-modal-panel',
        disableClose: true
      });

      if (dialogRef && dialogRef.afterClosed) {
        dialogRef.afterClosed().subscribe(result => {
          resolve(result === true);
        });
      } else {
        // Fallback para pruebas
        resolve(false);
      }
    });
  }

  /**
   * Muestra un modal de error
   */
  showErrorModal(mainMessage: string, subMessage: string, title?: string): void {
    this.dialog.open(ErrorModalComponent, {
      width: '450px',
      data: { mainMessage, subMessage, title },
      panelClass: 'custom-modal-panel',
      disableClose: false
    });
  }

  /**
   * Muestra un modal de éxito
   */
  showSuccessModal(message: string, title?: string): void {
    const dialogRef = this.dialog.open(SuccessModalComponent, {
      width: '450px',
      data: { message, title },
      panelClass: 'custom-modal-panel',
      disableClose: false
    });

    // Cerrar todos los modales cuando se cierre el modal de éxito
    dialogRef.afterClosed().subscribe(() => {
      this.dialog.closeAll();
    });
  }
}
